import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Button, Drawer, Heading, Loading } from "@foris/avocado-suite";
import { useLayoutStore } from "@context/useLayoutStore";
import { IGroup, ILeague } from "@modules/vacancies/contextVacancies/ContextVacancies";
import { MIGRATE_GROUP_MUTATION } from "@modules/vacancies/graphql/migrateGroup.queries";
import { CubeMutation, GroupValidationErrorUnion, Link } from "@models/ISchema";
import { emitEvent } from "@common/hooks/useListenEvent";
import GroupMigrationDrawerContent from "./GroupMigrationDrawerContent";
import useContextUrl from "@common/hooks/useContextUrl";
import styles from "./groupMigrationDrawer.module.scss";

interface GroupMigrationDrawerProps {
  isOpen: boolean;
  linkDetails?: ILeague;
  groupDetails?: IGroup;
  onClose: () => void;
}

const GroupMigrationDrawer = ({
  isOpen,
  onClose,
  linkDetails,
  groupDetails,
}: GroupMigrationDrawerProps) => {
  const { t } = useTranslation();
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);
  const [isLoading, setIsloading] = useState(false);
  const [migrateGroupMutation] = useMutation(MIGRATE_GROUP_MUTATION, {});
  const { origin, scenario } = useContextUrl();
  const [selectedLink, setSelectedLink] = useState<Link | null>(null);
  const [isMigrationValidated, setIsMigrationValidated] = useState(false);
  const [hasClassroomCapacityError, setHasClassroomCapacityError] = useState(false);
  const [hasSizeError, setHasSizeError] = useState(false);
  const [shouldAvoidErrors, setShouldAvoidErrors] = useState(false);

  const handleMigrateGroup = async () => {
    try {
      setIsloading(true);
      const data = await migrateGroupMutation({
        variables: {
          scenarioId: scenario,
          originId: origin,
          input: {
            linkId: selectedLink?.id,
            groupId: groupDetails?.id,
            dryRun: !isMigrationValidated,
            skipValidations: isMigrationValidated || shouldAvoidErrors,
          },
        },
      });

      const dataMutation: CubeMutation = data.data.cube;
      const validationErrors: Array<GroupValidationErrorUnion> =
        dataMutation.migrateGroup.validationErrors;

      if (
        validationErrors &&
        validationErrors.length > 0 &&
        !isMigrationValidated &&
        !shouldAvoidErrors
      ) {
        validationErrors.forEach((error: GroupValidationErrorUnion) => {
          if (error && error.__typename === "InvalidLinkChanges") {
            const hasComponentSizeError = error?.sectionChanges?.some(
              sectionChange =>
                !!sectionChange?.validationErrors?.find(
                  err => err.__typename === "InvalidComponentSize",
                ),
            );
            const hasInvalidClassroomCapacityError = error?.sessionChanges?.some(
              sessionChange =>
                !!sessionChange?.validationErrors?.find(
                  err => err.__typename === "InvalidClassroomCapacity",
                ),
            );

            setHasClassroomCapacityError(hasInvalidClassroomCapacityError);
            setHasSizeError(hasComponentSizeError);
          }
        });

        setIsloading(false);
        return;
      }

      if ((isMigrationValidated || shouldAvoidErrors) && !!dataMutation?.migrateGroup?.commited) {
        onClose?.();
        emitEvent("on:refresh", {
          target: "bundle-view",
        });
        setIsloading(false);
      }

      setIsMigrationValidated(true);
    } catch (error) {
      console.error(error);
      setIsloading(false);
    }
  };

  useEffect(() => {
    if (isMigrationValidated) {
      handleMigrateGroup();
    }
  }, [isMigrationValidated]);

  /**
   * Resets internal state when the drawer closes. The component stays
   * mounted regardless of `isOpen`, so without this cleanup the previous
   * session's state (selected link, validation flags, error warnings,
   * `shouldAvoidErrors` bypass) would leak into the next opening.
   */
  useEffect(() => {
    if (!isOpen) {
      setSelectedLink(null);
      setIsMigrationValidated(false);
      setHasClassroomCapacityError(false);
      setHasSizeError(false);
      setShouldAvoidErrors(false);
      setIsloading(false);
    }
  }, [isOpen]);

  const handleSelectLink = (value: Link | null) => {
    setSelectedLink(value);
    setIsMigrationValidated(false);
    setHasClassroomCapacityError(false);
    setHasSizeError(false);
    setShouldAvoidErrors(false);
  };

  return (
    <>
      {isLoading && <Loading />}

      <Drawer
        className={styles.groupMigrationDrawer}
        title={<Heading type="h3">{t("bundle.migrated-group-link.title")}</Heading>}
        isOpen={isOpen}
        onClose={onClose}
        heightGap={visibleHeaderPixels}
        closeOnOverlayClick
        footer={
          <div className={styles.drawerFooter}>
            <Button variant="ghost" onClick={onClose}>
              {t("bundle.migrated-group-link.forms.btn-cancel")}
            </Button>
            <Button
              disabled={
                !selectedLink?.id ||
                (!shouldAvoidErrors && (hasClassroomCapacityError || hasSizeError)) ||
                isLoading
              }
              onClick={handleMigrateGroup}
            >
              {t("bundle.migrated-group-link.forms.btn-save")}
            </Button>
          </div>
        }
      >
        {isOpen && (
          <GroupMigrationDrawerContent
            linkDetails={linkDetails}
            groupDetails={groupDetails}
            hasSizeError={hasSizeError}
            hasClassroomCapacityError={hasClassroomCapacityError}
            isLoading={isLoading}
            selectedLink={selectedLink}
            setSelectedLink={handleSelectLink}
            shouldAvoidErrors={shouldAvoidErrors}
            setShouldAvoidErrors={setShouldAvoidErrors}
          />
        )}
      </Drawer>
    </>
  );
};

export default GroupMigrationDrawer;
